/**
 * useStepSaveFactory.ts - Generic factory for step save hooks
 *
 * This module provides a factory pattern to eliminate duplication across
 * the various step-specific save hooks. Each step type has the same
 * save pattern but with different fields.
 *
 * Usage:
 * ```typescript
 * const { save } = useStepSave({
 *   step: tagStep,
 *   stepId,
 *   stepType: Api.AutomationStepType.AddTag,
 *   templateId,
 *   autoSave,
 *   buildUpdatedStep: (step, overrides) => ({
 *     ...step,
 *     tagName: overrides.newTagName ?? tagName,
 *     schedule: overrides.newSchedule ?? step?.schedule,
 *   }),
 * });
 * ```
 */

import * as Api from '@ViewModels';
import { useCallback } from 'react';
import type { useAutoSave } from './useAutoSave';

type UseAutoSaveReturn = ReturnType<typeof useAutoSave>;

/**
 * Base overrides that all step types support.
 */
export interface BaseStepOverrides {
	newSchedule?: Api.IAutomationStepSchedule;
}

/**
 * Configuration for the generic step save hook.
 */
export interface UseStepSaveConfig<
	TStep extends Api.IAutomationStep,
	TOverrides extends BaseStepOverrides = BaseStepOverrides,
> {
	/** Current step data */
	step: TStep | null;
	/** Step ID for persistence */
	stepId: string | undefined;
	/** Automation step type */
	stepType: Api.AutomationStepType;
	/** Template ID for persistence */
	templateId: string | undefined;
	/** Auto-save hook instance */
	autoSave: UseAutoSaveReturn;
	/**
	 * Function to build the updated step from current state and overrides.
	 * This is where step-specific field handling goes.
	 */
	buildUpdatedStep: (step: TStep | null, overrides: TOverrides) => Partial<TStep>;
	/** Optional dependencies for the save callback (in addition to default ones) */
	additionalDeps?: unknown[];
}

/**
 * Return type for the generic step save hook.
 */
export interface UseStepSaveReturn<TOverrides extends BaseStepOverrides> {
	/** Save function that accepts optional overrides */
	save: (overrides?: TOverrides) => Promise<void>;
}

/**
 * Generic step save hook that eliminates duplication across step-specific save hooks.
 *
 * All step save hooks follow the same pattern:
 * 1. Check stepId and templateId exist
 * 2. Merge overrides with current state
 * 3. Build updated step model
 * 4. Call autoSave.saveStep
 *
 * This factory captures that pattern, with step-specific logic in buildUpdatedStep.
 */
export function useStepSave<
	TStep extends Api.IAutomationStep,
	TOverrides extends BaseStepOverrides = BaseStepOverrides,
>({
	step,
	stepId,
	stepType,
	templateId,
	autoSave,
	buildUpdatedStep,
	additionalDeps = [],
}: UseStepSaveConfig<TStep, TOverrides>): UseStepSaveReturn<TOverrides> {
	const save = useCallback(
		(overrides: TOverrides = {} as TOverrides): Promise<void> => {
			if (!step || !stepId || !templateId) {
				return Promise.resolve();
			}

			const updatedStep = buildUpdatedStep(step, overrides);

			// Apply schedule if provided in overrides
			if (overrides.newSchedule) {
				updatedStep.schedule = overrides.newSchedule;
			}

			return autoSave.saveStep(stepId, stepType, updatedStep as Api.IAutomationStep);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[step, stepId, stepType, templateId, autoSave, buildUpdatedStep, ...additionalDeps]
	);

	return { save };
}

/**
 * Extended configuration for steps that need onSaveStep functionality.
 * Used by Email and Texting steps that allow saving partial step updates.
 */
export interface UseStepSaveWithOnSaveConfig<TStep extends Api.IAutomationStep>
	extends Omit<UseStepSaveConfig<TStep>, 'buildUpdatedStep'> {
	/** Function to build updated step - simpler version for onSaveStep pattern */
	buildUpdatedStep?: (step: TStep | null, overrides: BaseStepOverrides) => Partial<TStep>;
}

/**
 * Return type for steps with onSaveStep functionality.
 */
export interface UseStepSaveWithOnSaveReturn<TStep extends Api.IAutomationStep> {
	/** Save function for partial updates */
	onSaveStep: (stepToSave?: Partial<TStep>, newSchedule?: Api.IAutomationStepSchedule) => Promise<void>;
}

/**
 * Hook for steps that need the onSaveStep pattern (Email, Texting).
 * This provides a simpler save interface for direct step model updates.
 */
export function useStepSaveWithOnSave<TStep extends Api.IAutomationStep>({
	step,
	stepId,
	stepType,
	templateId,
	autoSave,
}: UseStepSaveWithOnSaveConfig<TStep>): UseStepSaveWithOnSaveReturn<TStep> {
	const onSaveStep = useCallback(
		(stepToSave?: Partial<TStep>, newSchedule?: Api.IAutomationStepSchedule) => {
			if (!step || !stepId || !templateId) {
				return Promise.resolve();
			}

			const stepModel: TStep = {
				...step,
				...(stepToSave || {}),
			};

			if (newSchedule) {
				stepModel.schedule = newSchedule;
			}

			return autoSave.saveStep(stepId, stepType, stepModel);
		},
		[step, stepId, stepType, templateId, autoSave]
	);

	return { onSaveStep };
}
